import { useCallback, useEffect, useState } from "react";
import { Reveal } from "@/components/atoms";
import { AdminContent } from "@/components/AdminContent";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "cv-admin-token";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export const AdminPage = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("anfragen");

  const load = useCallback(async (t) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/cv-requests`, { headers: { "X-Admin-Token": t } });
      if (res.status === 401) {
        setError("Token ungültig.");
        setRows(null);
        sessionStorage.removeItem(TOKEN_KEY);
        return;
      }
      if (!res.ok) throw new Error();
      const data = await res.json();
      setRows(data);
      sessionStorage.setItem(TOKEN_KEY, t);
    } catch {
      setError("Laden fehlgeschlagen — bitte später erneut versuchen.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setRows(null);
  };

  const downloadCv = async (which) => {
    try {
      const res = await fetch(`${API}/api/cv-file/${which}`, { headers: { "X-Admin-Token": sessionStorage.getItem(TOKEN_KEY) || "" } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cv-${which}.${which.endsWith("-docx") ? "docx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Download fehlgeschlagen.");
    }
  };

  return (
    <div className="min-h-screen px-5 md:px-10 py-16 md:py-24" data-testid="admin-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3">
          <span className="w-2 h-2 bg-proof" aria-hidden="true" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-smoke">Jo Lenz — Intern · nicht öffentlich</span>
        </div>
        <h1 className="mt-6 font-serif font-light tracking-tight text-4xl md:text-6xl">CV-Anfragen</h1>

        {rows !== null && (
          <div className="mt-8 flex gap-2" data-testid="admin-tabs">
            {[["anfragen", "Anfragen"], ["inhalte", "Inhalte"]].map(([key, label]) => (
              <button
                key={key}
                data-testid={`admin-tab-${key}`}
                onClick={() => setTab(key)}
                className={`px-5 py-3 font-mono text-[10px] uppercase tracking-[0.25em] border transition-colors duration-300 ${
                  tab === key ? "border-ink bg-ink text-paper" : "border-ink/30 text-smoke hover:border-ink hover:text-ink"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {rows === null ? (
          <Reveal className="mt-12 max-w-md">
            <form
              data-testid="admin-login-form"
              onSubmit={(e) => {
                e.preventDefault();
                load(token.trim());
              }}
              className="border border-ink bg-paper p-8"
            >
              <label htmlFor="admin-token" className="font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">
                Zugangs-Token
              </label>
              <input
                id="admin-token"
                data-testid="admin-token-input"
                type="password"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                className="mt-3 w-full border-b border-ink/30 bg-transparent py-3 text-base font-light focus:border-proof focus:outline-none transition-colors duration-300"
                autoComplete="off"
              />
              {error && (
                <p data-testid="admin-error" className="mt-4 font-mono text-[11px] text-proof">{error}</p>
              )}
              <button
                data-testid="admin-login-button"
                type="submit"
                disabled={loading || !token.trim()}
                className="mt-8 w-full border border-ink bg-ink text-paper px-7 py-4 font-mono text-[11px] uppercase tracking-[0.25em] transition-colors duration-500 hover:bg-transparent hover:text-ink disabled:opacity-50"
              >
                {loading ? "Prüfe …" : "Anmelden"}
              </button>
            </form>
          </Reveal>
        ) : tab === "inhalte" ? (
          <AdminContent token={sessionStorage.getItem(TOKEN_KEY) || ""} />
        ) : (
          <div className="mt-12">
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-smoke" data-testid="admin-count">
                {rows.length} {rows.length === 1 ? "Anfrage" : "Anfragen"}
              </span>
              <button
                data-testid="admin-refresh-button"
                onClick={() => load(sessionStorage.getItem(TOKEN_KEY) || "")}
                className="border border-ink/30 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke transition-colors duration-300 hover:border-ink hover:text-ink"
              >
                Aktualisieren
              </button>
              <button
                data-testid="admin-logout-button"
                onClick={logout}
                className="border border-ink/30 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke transition-colors duration-300 hover:border-proof hover:text-proof"
              >
                Abmelden
              </button>
            </div>

            <div className="mb-10" data-testid="admin-cv-downloads">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">Lebensläufe — Direkt-Download</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {[
                  ["main-de", "Project & Operations · DE"],
                  ["main-en", "Project & Operations · EN"],
                  ["office-de", "Customer & Office · DE"],
                  ["office-en", "Customer & Office · EN"],
                  ["ped-de", "People & Care · DE"],
                  ["ped-en", "People & Care · EN"],
                  ["letter-de", "Anschreiben · DE"],
                  ["letter-en", "Cover Letter · EN"],
                  ["main-de-docx", "Project & Operations · DE · editierbar"],
                  ["main-en-docx", "Project & Operations · EN · editierbar"],
                  ["office-de-docx", "Customer & Office · DE · editierbar"],
                  ["office-en-docx", "Customer & Office · EN · editierbar"],
                  ["ped-de-docx", "People & Care · DE · editierbar"],
                  ["ped-en-docx", "People & Care · EN · editierbar"],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    data-testid={`admin-cv-download-${key}`}
                    onClick={() => downloadCv(key)}
                    className="border border-ink/30 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/80 transition-colors duration-300 hover:bg-ink hover:text-paper"
                  >
                    {label} ↓
                  </button>
                ))}
              </div>
            </div>

            <div className="border-t border-hairline" data-testid="admin-table">
              <div className="hidden md:grid grid-cols-12 gap-4 py-3 font-mono text-[9px] uppercase tracking-[0.2em] text-smoke/70">
                <span className="col-span-2">Datum</span>
                <span className="col-span-2">Name</span>
                <span className="col-span-3">E-Mail</span>
                <span className="col-span-2">Unternehmen</span>
                <span className="col-span-1">Sprache</span>
                <span className="col-span-2">Status</span>
              </div>
              {rows.map((r) => (
                <div
                  key={r.id}
                  data-testid={`admin-row-${r.id}`}
                  className="grid md:grid-cols-12 gap-1 md:gap-4 py-4 border-t border-hairline text-sm font-light"
                >
                  <span className="md:col-span-2 font-mono text-[11px] text-smoke">{fmtDate(r.created_at)}</span>
                  <span className="md:col-span-2 font-medium">{r.name}</span>
                  <a href={`mailto:${r.email}`} className="md:col-span-3 text-ink/80 link-underline w-fit">{r.email}</a>
                  <span className="md:col-span-2 text-ink/70">{r.company || "—"}</span>
                  <span className="md:col-span-1 font-mono text-[11px] uppercase text-smoke">{r.lang}</span>
                  <span className="md:col-span-2 font-mono text-[10px] uppercase tracking-[0.1em]">
                    {r.sent ? <span className="text-proof">CV gesendet</span> : <span className="text-smoke">offen</span>}
                    {r.downloaded_at && <span className="block text-smoke">↓ {fmtDate(r.downloaded_at)}</span>}
                  </span>
                </div>
              ))}
              {rows.length === 0 && (
                <p className="py-10 text-sm font-light text-smoke">Noch keine Anfragen eingegangen.</p>
              )}
            </div>
          </div>
        )}

        <p className="mt-16 font-mono text-[9px] uppercase tracking-[0.25em] text-smoke/60">
          work.jolenz.art · interner Bereich
        </p>
      </div>
    </div>
  );
};
